/**
 * Utility functions for handling colors in MUI components
 */

/**
 * Valid MUI color options for components that need full palette objects
 */
const VALID_MUI_COLORS = [
  'default',
  'primary', 
  'secondary', 
  'error', 
  'info', 
  'success', 
  'warning'
];

/**
 * MUI colors that have contrastText property - safer subset for Timeline components
 */
const FULL_PALETTE_COLORS = [
  'primary', 
  'secondary', 
  'error', 
  'info', 
  'success', 
  'warning'
];

/**
 * Ensures a color value is valid for MUI components
 * @param {string} color - The color value to sanitize
 * @param {string} fallback - The fallback color if invalid (default: 'default')
 * @param {boolean} requireContrastText - Whether to require colors that have contrastText (for Timeline components)
 * @returns {string} A safe color value
 */
export const getSafeColor = (color, fallback = 'default', requireContrastText = false) => {
  if (!color) return requireContrastText ? 'primary' : fallback;
  
  // Use appropriate color list based on requirements
  const validColors = requireContrastText ? FULL_PALETTE_COLORS : VALID_MUI_COLORS;
  
  // If it's already a valid color, return it
  const lowerColor = color.toLowerCase();
  if (validColors.includes(lowerColor)) {
    return lowerColor;
  }
  
  // Handle common mappings
  const colorMap = {
    // Status colors
    'ASSIGNED': requireContrastText ? 'primary' : 'default',
    'IN_PROGRESS': 'primary',
    'COMPLETED': 'success',
    'REJECTED': 'error',
    'SKIPPED': 'warning',
    'FORWARDED': 'warning',
    'RECEIVED': 'info',
    'REJECTED_RECEIPT': 'error',
    'PENDING': requireContrastText ? 'primary' : 'default',
    'PROCESSING': 'primary',
    'APPROVED': 'success',
    'DECLINED': 'error',
    
    // Priority colors
    'LOW': 'success',
    'MEDIUM': 'info',
    'HIGH': 'warning',
    'URGENT': 'error',
    'NORMAL': 'info',
    'CRITICAL': 'error',
    
    // Special cases
    'grey': requireContrastText ? 'primary' : 'default',
    'gray': requireContrastText ? 'primary' : 'default'
  };
  
  // Try to map the color, using appropriate fallback
  return colorMap[color?.toUpperCase()] || (requireContrastText ? 'primary' : fallback);
};

/**
 * Colors for different status values
 */
export const statusColors = {
  ASSIGNED: 'default',
  IN_PROGRESS: 'primary',
  COMPLETED: 'success',
  REJECTED: 'error',
  SKIPPED: 'warning',
  FORWARDED: 'warning',
  RECEIVED: 'info',
  REJECTED_RECEIPT: 'error',
  PENDING: 'default',
  PROCESSING: 'primary',
  APPROVED: 'success',
  DECLINED: 'error'
};

/**
 * Colors for different priority values
 */
export const priorityColors = {
  LOW: 'success',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
  NORMAL: 'info',
  CRITICAL: 'error'
};

export default {
  getSafeColor,
  statusColors,
  priorityColors
}; 